import { createVesselProvisioningRepository } from "./VesselProvisioningRepository";
import { buildOrganizationSeed } from "../../Contexts/v2VoyageWorkspace";
import { oceanLogixDexieDb } from "../../data/local/db";
import { runLocalTransaction } from "../../data/local/db/runLocalTransaction";
import { TABLES } from "../../data/local/db/schema";
import { getCompanyInformation } from "../../utils/GetCompanyInformation";
import { createOrganization } from "../../domain/entities";
import { localAuthService } from "../auth/localAuthService";

const formatSyncTimestamp = (isoString) => {
  const date = new Date(isoString);
  if (Number.isNaN(date.getTime())) return "";

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");

  return `${year}-${month}-${day} ${hours}:${minutes}`;
};

const buildTankLayoutBucket = (tanks) =>
  Object.fromEntries(
    tanks.map((tank) => [
      tank.name,
      {
        cube: tank.capacity,
        capacity: tank.capacity,
        coating: tank.coating,
        ...tank.properties,
      },
    ])
  );

const buildProvisionedVesselContext = ({
  vessel,
  vesselParticulars,
  tanks,
}) => {
  const identifiers = {
    ...(vesselParticulars?.identifiers || {}),
    vesselName:
      vesselParticulars?.identifiers?.vesselName || vessel.name || "",
    imoNumber:
      vesselParticulars?.identifiers?.imoNumber || vessel.imoNumber || "",
    callSign:
      vesselParticulars?.identifiers?.callSign || vessel.callSign || "",
    vesselType:
      vesselParticulars?.identifiers?.vesselType || vessel.vesselType || "",
  };

  const cargoTanks = tanks.filter((tank) => tank.tankType === "cargo");
  const ballastTanks = tanks.filter((tank) => tank.tankType === "ballast");
  const otherTanks = tanks.filter(
    (tank) => tank.tankType !== "cargo" && tank.tankType !== "ballast"
  );

  return {
    id: vessel.id,
    vesselId: vessel.id,
    organizationId: vessel.organizationId,
    name: vessel.name,
    imoNumber: vessel.imoNumber || identifiers.imoNumber || "",
    callSign: vessel.callSign || identifiers.callSign || "",
    vesselType: vessel.vesselType || identifiers.vesselType || "",
    enabledModules: vessel.enabledModules || [],
    particulars: vesselParticulars,
    owner: vesselParticulars?.owner || {},
    operator: vesselParticulars?.operator || {},
    tanks,
    configuration: {
      identifiers,
      dimensions: vesselParticulars?.dimensions || {},
      capacities: vesselParticulars?.capacities || {},
      performance: vesselParticulars?.performance || {},
      tankLayout: {
        cargoTanks: buildTankLayoutBucket(cargoTanks),
        ballastTanks: buildTankLayoutBucket(ballastTanks),
        otherTanks: buildTankLayoutBucket(otherTanks),
      },
    },
    contacts: {
      owner: vesselParticulars?.owner || {},
      operator: vesselParticulars?.operator || {},
    },
  };
};

const persistLocalCanonicalVesselWorkspaceInTransaction = async ({
  db,
  vessel,
  vesselParticulars,
  tanks,
  resolveCompanyInformation = getCompanyInformation,
}) => {
  const { companyName } = resolveCompanyInformation();
  const organizationSeed = buildOrganizationSeed(companyName);
  const organization = createOrganization({
    id: vessel.organizationId || organizationSeed.id,
    slug: vessel.organizationId || organizationSeed.slug,
    name: companyName || organizationSeed.name,
    enabledModules: vessel.enabledModules || organizationSeed.enabledModules,
  });

  await db.organizations.put(organization);
  await db.vessels.put({
    ...vessel,
    organizationId: vessel.organizationId || organization.id,
  });

  if (vesselParticulars) {
    await db.vesselParticulars.put(vesselParticulars);
  } else {
    await db.vesselParticulars.delete(vessel.id);
  }

  const existingTanks = await db.tanks.where("vesselId").equals(vessel.id).toArray();
  const incomingTankIds = new Set(tanks.map((tank) => tank.id));

  await Promise.all(
    existingTanks
      .filter((tank) => !incomingTankIds.has(tank.id))
      .map((tank) => db.tanks.delete(tank.id))
  );

  if (tanks.length > 0) {
    await db.tanks.bulkPut(tanks);
  }
};

const replaceLocalProvisioningCache = ({
  syncedAtDisplay,
  syncedAtIso,
  vesselId,
  vesselContext,
  credentialPackageVersion,
}) => {
  localStorage.setItem("lastSyncTime", syncedAtDisplay);
  localStorage.setItem("lastSyncTimeIso", syncedAtIso);
  localStorage.setItem("provisionedVesselId", vesselId);
  localStorage.setItem("activeShipData", JSON.stringify(vesselContext));
  localStorage.setItem(
    "activeCredentialPackageVersion",
    String(credentialPackageVersion)
  );
};

export const listProvisionableVessels = async (
  repository = createVesselProvisioningRepository()
) => repository.listAvailableVessels();

export const provisionUsersForVessel = async (
  vesselId,
  repository = createVesselProvisioningRepository(),
  {
    authService = localAuthService,
    db = oceanLogixDexieDb,
    resolveCompanyInformation = getCompanyInformation,
  } = {}
) => {
  if (!vesselId) {
    throw new Error("No vessel selected");
  }

  const vessel = await repository.getVesselDataById(vesselId);
  const vesselParticulars = await repository.getVesselParticularsByVesselId(
    vesselId
  );
  const tanks = await repository.listTanksByVesselId(vesselId);
  const vesselAccess = await repository.getActiveVesselAccess(vesselId);
  const activeAccess = vesselAccess.filter(
    (access) => (access.status || "active") === "active"
  );
  const userIds = [
    ...new Set(activeAccess.map((access) => access.userId).filter(Boolean)),
  ];
  const { users, missingUserIds } = await repository.getUsersByIds(userIds);
  const vesselContext = buildProvisionedVesselContext({
    vessel,
    vesselParticulars,
    tanks,
  });

  if (missingUserIds.length > 0) {
    console.warn(
      "[Vessel provisioning] Missing user references for vesselAccess records",
      {
        vesselId,
        missingUserIds,
      }
    );
  }

  if (!vesselParticulars) {
    console.warn(
      "[Vessel provisioning] VesselParticulars missing for canonical vessel",
      {
        vesselId,
      }
    );
  }

  const syncedAtIso = new Date().toISOString();
  const syncedAtDisplay = formatSyncTimestamp(syncedAtIso);

  const { credentialPackage, mirroredUsers } = await runLocalTransaction(
    db,
    async () => {
      await persistLocalCanonicalVesselWorkspaceInTransaction({
        db,
        vessel,
        vesselParticulars,
        tanks,
        resolveCompanyInformation,
      });

      const activationResult =
        await authService.activateCredentialPackageWithinTransaction({
          vesselId,
          users,
          syncedAt: syncedAtIso,
          txDb: db,
        });

      return {
        credentialPackage: activationResult.credentialPackage,
        mirroredUsers: users.map((user) => ({
          id: user.id,
          userName: user.userName || user.username || user.id,
          firstName: [user.firstName, user.lastName].filter(Boolean).join(" ").trim(),
          lastName: "",
          email: "",
          roles: Array.isArray(user.roles)
            ? user.roles
            : user.role
              ? [user.role]
              : [],
          password: user.password,
          status: user.status || "active",
        })),
      };
    },
    [
      TABLES.ORGANIZATIONS,
      TABLES.VESSELS,
      TABLES.VESSEL_PARTICULARS,
      TABLES.TANKS,
      TABLES.CREDENTIAL_PACKAGES,
      TABLES.VESSEL_INSTALLATIONS,
    ]
  );

  localStorage.setItem("users", JSON.stringify(mirroredUsers));

  replaceLocalProvisioningCache({
    syncedAtDisplay,
    syncedAtIso,
    vesselId,
    vesselContext,
    credentialPackageVersion: credentialPackage.version,
  });

  const warnings = [];
  if (userIds.length === 0) {
    warnings.push({ code: "no-active-access" });
  }
  if (!vesselParticulars) {
    warnings.push({ code: "missing-vessel-particulars" });
  }
  if (missingUserIds.length > 0) {
    warnings.push({
      code: "missing-users",
      count: missingUserIds.length,
      userIds: missingUserIds,
    });
  }

  return {
    shipData: vesselContext,
    userList: mirroredUsers,
    syncedAtDisplay,
    syncedAtIso,
    credentialPackage,
    warnings,
  };
};
